import {
  CategoryLeaderboardConstructRequestObject,
  CategoryLeaderboardParam,
  CategoryLeaderboardPgPage,
  CategoryLeaderboardReq,
} from '@models/CategoryLeaderboard';
import axios from 'axios';
import { environment } from '@environments/environment';
import { getPeriodMetaFactory } from '@utils/PeriodUtils';
import { ProductDataType } from '@pages/product-analyzer/Product';
import { getCollectionsLiveDataParams } from '@utils/collectionUtils/CollectionUtils';
import { CATEGORY_FEATURE_OPTION } from '@utils/hook/selector/ShowPerformanceWithinSelector';
import { ExploreTrendsStackParams, ExportExploreTrendsStack } from '@data-visualization/trends-stack-chart/Types';
import {
  ExportPerformanceBySegmentResp,
  PerformanceAnalysisParam,
} from '@data-visualization/performance-analysis-line-chart/Types';
import { FilterCriteriaParam } from '@models/CommonParam';
import { StackedChartResp } from '@data-visualization/stacked-chart/Types';
import { constructSegmentStackedChartRequest } from '@services/explore/ExploreRequestUtils';
import { sortLegendDataAscending } from '@data-visualization/utilities/ChartUtils';
import {
  applyParamsBasedOnFilterPOST,
  generateParamExploreTrendsStack,
  generateParamPA,
  paramsSerializer,
} from '@services/utils/OverviewServiceUtils';
import {
  preprocessExploreLeaderboardResponse,
  preprocessExploreStackChartResponse,
  preprocessExploreTrendsResponse,
  preprocessExploreTrendsStackResponse,
} from './ExploreResponseUtils';
import { getQueryClient } from '@queries/QueryClientWrapper';
import { throwApiError } from '@services/utils/ErrorHandlers';

const scope = 'explore';

export const getExploreLeaderboard = async (
  param: CategoryLeaderboardParam,
  isExport?: boolean,
): Promise<CategoryLeaderboardPgPage> => {
  let params: (CategoryLeaderboardReq & { excludedBrand?: string[] }) | undefined;

  try {
    const base = 'explore-leaderboard';
    const qc = getQueryClient(scope);
    const requestObject: CategoryLeaderboardConstructRequestObject = {
      period: param.period,
      comparisonPeriod: param.comparisonPeriod,
      clientId: param.clientId,
      productGroup: param.productGroup,
      countryIds: param.countries.map((country) => country.id),
      productType: param.productType,
      shareType: param.shareType,
      periodType: param.periodType,
      showPerformance: param.showPerformance,
      groups: param.groups,
      isUsingCollectionLiveData: param.isUsingCollectionLiveData,
      ...(param.dataType !== CATEGORY_FEATURE_OPTION.dataType && { dataType: param.dataType }),
    };

    const sortKey = param.sortOption.label?.toUpperCase();

    params = constructRequest(requestObject);
    applyParamsBasedOnFilterPOST(params, param.appliedFilters);

    const queryParams = {
      page: param.pageRequest.page,
      size: param.pageRequest.size,
      sortBy: param.sortOption.headerKey,
      sortDirection: param.sortOption.sortDir,
      sortKey,
      clientId: param.clientId,
      growthType: param.growthType.toUpperCase(),
      periodType: param.periodType.toUpperCase(),
      country: param.countries[0]?.id,
    };

    const queryKey = [base, params, queryParams];

    const queryFn = async ({ signal }) => {
      const { data } = await axios.post<CategoryLeaderboardPgPage>(
        `${environment.baseUrl}/v2/explore/leaderboard`,
        params,
        {
          params: queryParams,
          signal,
        },
      );
      preprocessExploreLeaderboardResponse(data, param.countries, param.dataType);
      return data;
    };

    if (isExport) {
      const controller = new AbortController();
      return await queryFn({ signal: controller.signal });
    }

    return await qc.ensureQueryData({ queryKey, queryFn });
  } catch (err: any) {
    // Delegated error handling; helper always throws. Added explicit throw for Sonar compliance.
    throwApiError(err, { excludedBrand: params?.excludedBrand }); // NOSONAR
  }
};

export const getExploreTrends = async (param: PerformanceAnalysisParam): Promise<ExportPerformanceBySegmentResp> => {
  try {
    const queryParamsExport = generateParamPA(param);
    if (queryParamsExport.showPerformance === 'Category') {
      // @ts-ignore
      queryParamsExport.dataType = undefined; //for collection
    }

    const base = 'explore-trends';
    const qc = getQueryClient(scope);
    const queryKey = [base, queryParamsExport];

    return await qc.ensureQueryData({
      queryKey,
      queryFn: async ({ signal }) => {
        const { data } = await axios.post<ExportPerformanceBySegmentResp>(
          `${environment.baseUrl}/v2/explore/trends`,
          queryParamsExport,
          {
            paramsSerializer: paramsSerializer,
            signal,
          },
        );
        preprocessExploreTrendsResponse(data, param.countries, param.dataType);
        return data;
      },
    });
  } catch (err: any) {
    console.error(`An error has occurred: ${err}`);
    throw new Error(err);
  }
};

export const getExploreTrendsStackChart = async (
  param: ExploreTrendsStackParams,
  isExcelExport?: boolean,
): Promise<ExportExploreTrendsStack> => {
  try {
    const queryParamsExport = generateParamExploreTrendsStack(param);
    if (queryParamsExport.showPerformance === 'Category') {
      // @ts-ignore
      queryParamsExport.dataType = undefined; //for collection
    }
    const base = 'explore-trends-stacked-chart';
    const qc = getQueryClient(scope);
    const queryKey = [base, queryParamsExport];

    return await qc.ensureQueryData({
      queryKey,
      queryFn: async ({ signal }) => {
        const { data } = await axios.post<ExportExploreTrendsStack>(
          `${environment.baseUrl}/v2/explore/trends/trend-stacked-chart${isExcelExport ? '?isExcelExport=true' : ''}`,
          queryParamsExport,
          {
            paramsSerializer,
            signal,
          },
        );

        preprocessExploreTrendsStackResponse(data, param.countries, param.dataType);
        return data;
      },
    });
  } catch (err: any) {
    console.error(`An error has occurred: ${err}`);
    throw new Error(err);
  }
};

export const getExploreSegmentsStackedChart = async (param: FilterCriteriaParam): Promise<StackedChartResp[]> => {
  const base = 'explore-segments-stacked-chart';
  const qc = getQueryClient(scope);
  const queryKey = [base, param];

  return await qc.ensureQueryData({
    queryKey,
    queryFn: async ({ signal }) => {
      const { data } = await axios
        .post<StackedChartResp[]>(
          `${environment.baseUrl}/v2/explore/segments/stacked-chart`,
          constructSegmentStackedChartRequest(param),
          {
            params: {},
            signal,
          },
        )
        .catch((err: Error) => {
          throw new Error(`An error has occurred: ${err}`);
        });
      preprocessExploreStackChartResponse(data, param.countries, param.segmentDataType, param.segmentByDataType);
      return data.length > 0 ? sortLegendDataAscending(data) : [];
    },
  });
};

const constructRequest = (param: CategoryLeaderboardConstructRequestObject): CategoryLeaderboardReq => {
  const req = {} as CategoryLeaderboardReq;

  const { periodType, comparisonPeriod, isUsingCollectionLiveData = false } = param;
  const periodMetaForExplore = getPeriodMetaFactory(param.period, comparisonPeriod, periodType);

  req.country = param.countryIds;
  req.clientId = param.clientId;
  if (param.productGroup && param.productGroup.length !== 0) {
    req.productGroup = param.productGroup.map((option) => {
      return option.value;
    });
  }

  req.productDataType = ProductDataType.PerformanceAnalyzer;
  req.productType = param.productType;
  req.periodType = periodType.toUpperCase().replace(/\s/g, '');
  req.period = periodMetaForExplore ? periodMetaForExplore.currentPeriod.toUpperCase() : '';
  req.comparisonPeriod = periodMetaForExplore ? periodMetaForExplore.comparisonPeriod.toUpperCase() : '';
  req.shareType = param.shareType;
  req.showPerformance = param.showPerformance;
  req.dataType = param.dataType;

  if (param.groups && param.groups.length !== 0) {
    req.groups = param.groups;
  }

  const collectionsParam = getCollectionsLiveDataParams(isUsingCollectionLiveData, periodType, comparisonPeriod);

  return {
    ...req,
    ...collectionsParam,
  };
};
